import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Edit2, Check, Loader2, Camera, FileText, Heart } from 'lucide-react';
import { toast } from 'sonner';
import { useQuery, useMutation } from '@tanstack/react-query';
import { AppHeader } from '@/components/layout/AppHeader';

interface PhotoData {
  url: string;
  order: number;
  scanStatus: 'pending' | 'safe' | 'warning' | 'blocked';
}

interface LifestylePreferences {
  socialLevel?: number;
  weekendEnergy?: number;
  datingPace?: number;
  communicationStyle?: number;
}

interface Profile {
  id: string;
  userId: string;
  displayName: string;
  age: number;
  bio: string | null;
  lookingFor: string | null;
  photos: PhotoData[];
  lifestylePreferences: LifestylePreferences | null;
  aiPersonalityVector: any;
  aiProcessedAt: string | null;
  profileCompletion: number;
  createdAt: string;
  updatedAt: string;
}

interface ProfileResponse {
  profile: Profile;
  type: 'teacher' | 'non_educator';
}

// Helper to get lifestyle label
function getLifestyleLabel(value: number | undefined, type: 'social' | 'weekend' | 'pace' | 'communication'): string {
  if (value === undefined) return 'Not set';

  const labels = {
    social: ['Introvert', 'Moderate Introvert', 'Moderate Extrovert', 'Extrovert'],
    weekend: ['Homebody', 'Mostly Relaxed', 'Mostly Active', 'Always Out'],
    pace: ['Very Slow', 'Slow & Steady', 'Moderate', 'Fast-Paced'],
    communication: ['Brief & Casual', 'Balanced', 'Conversational', 'Deep & Chatty'],
  };

  const categoryLabels = labels[type];
  if (value <= 25) return categoryLabels[0];
  if (value <= 50) return categoryLabels[1];
  if (value <= 75) return categoryLabels[2];
  return categoryLabels[3];
}

export const ProfilePreviewPage: React.FC = () => {
  const navigate = useNavigate();
  const { getToken } = useAuth();

  // Fetch profile data
  const { data, isLoading, error } = useQuery({
    queryKey: ['profile', 'me'],
    queryFn: async () => {
      const token = await getToken();
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:8787';
      const response = await fetch(`${apiUrl}/api/profiles/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Failed to fetch profile');
      return response.json() as Promise<ProfileResponse>;
    },
  });

  // Complete profile mutation
  const completeProfileMutation = useMutation({
    mutationFn: async () => {
      const token = await getToken();
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:8787';
      const response = await fetch(`${apiUrl}/api/profiles/me`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ profileCompletion: 100 }),
      });
      if (!response.ok) throw new Error('Failed to complete profile');
      return response.json();
    },
    onSuccess: () => {
      toast.success('Profile completed! Welcome to Hall Pass!');
      navigate('/feed');
    },
    onError: (error) => {
      console.error('Complete profile error:', error);
      toast.error('Failed to complete profile. Please try again.');
    },
  });

  const handleCompleteProfile = () => {
    completeProfileMutation.mutate();
  };

  const handleBack = () => {
    navigate(-1);
  };

  if (isLoading) {
    return (
      <>
        <AppHeader />
        <div className="min-h-screen bg-brand-background flex items-center justify-center pt-16">
          <Card className="w-full max-w-md shadow-xl bg-brand-surface border-brand-border">
            <CardContent className="flex flex-col items-center gap-4 py-12">
              <Loader2 className="h-12 w-12 animate-spin text-brand-primary" />
              <p className="text-lg text-gray-300">Loading your profile...</p>
            </CardContent>
          </Card>
        </div>
      </>
    );
  }

  if (error || !data) {
    return (
      <>
        <AppHeader />
        <div className="min-h-screen bg-brand-background flex items-center justify-center p-4 pt-16">
          <Card className="w-full max-w-md shadow-xl bg-brand-surface border-brand-border">
            <CardContent className="py-12 text-center">
              <p className="text-lg text-red-400 mb-4">Failed to load profile</p>
              <Button onClick={() => navigate('/profile/photos')} className="bg-brand-primary hover:bg-brand-primary/90 text-white">
                Start Over
              </Button>
            </CardContent>
          </Card>
        </div>
      </>
    );
  }

  const { profile } = data;
  // Show all uploaded photos - scan warnings during upload are sufficient
  const safePhotos = profile.photos;
  const hasPhotos = safePhotos.length > 0;
  const hasBio = !!profile.bio?.trim();
  const hasLookingFor = !!profile.lookingFor?.trim();
  const hasLifestyle = !!profile.lifestylePreferences;
  const isComplete = hasPhotos && hasBio && hasLookingFor && hasLifestyle;

  return (
    <>
      <AppHeader />
      <div className="min-h-screen bg-brand-background py-8 px-4 pt-16">
        <div className="w-full max-w-3xl mx-auto space-y-6">
        {/* Header Card */}
        <Card className="shadow-xl bg-brand-surface border-brand-border">
          <CardHeader className="text-center">
            <CardTitle className="text-3xl font-bold text-white">
              Profile Preview
            </CardTitle>
            <CardDescription className="text-lg mt-2 text-gray-400">
              Review your profile before completing
            </CardDescription>
          </CardHeader>

          <CardContent className="space-y-6">
            {/* Completion Status */}
            {!isComplete && (
              <div className="p-4 bg-amber-500/10 border border-amber-500/20 rounded-lg">
                <p className="text-sm text-amber-200 font-medium">
                  <strong>Incomplete Profile:</strong> Please complete all sections before finishing your profile.
                </p>
              </div>
            )}

            {/* AI Processing Status */}
            {profile.aiProcessedAt && (
              <div className="p-3 bg-purple-500/10 border border-purple-500/20 rounded-lg">
                <p className="text-sm text-purple-200">
                  ✨ <strong>AI Analysis Complete:</strong> Your profile has been analyzed for personality matching
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Photos Section */}
        <Card className="shadow-xl bg-brand-surface border-brand-border">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Camera className="h-5 w-5 text-brand-primary" />
                <CardTitle className="text-xl text-white">Photos</CardTitle>
                {hasPhotos && <Badge variant="secondary" className="ml-2 bg-green-500/20 text-green-300 hover:bg-green-500/30">✓</Badge>}
              </div>
              <Button
                onClick={() => navigate('/profile/photos')}
                variant="ghost"
                size="sm"
                className="flex items-center gap-1 text-gray-400 hover:text-white hover:bg-white/10"
              >
                <Edit2 className="h-3 w-3" />
                Edit
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {hasPhotos ? (
              <div className="grid grid-cols-3 md:grid-cols-5 gap-3">
                {safePhotos.map((photo, index) => (
                  <div
                    key={photo.url}
                    className="relative aspect-square rounded-lg overflow-hidden border-2 border-brand-border"
                  >
                    <img
                      src={photo.url}
                      alt={`Photo ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                    {index === 0 && (
                      <div className="absolute top-1 left-1 bg-brand-primary text-white text-xs px-2 py-0.5 rounded">
                        Primary
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500">No photos uploaded yet</p>
            )}
          </CardContent>
        </Card>

        {/* Bio Section */}
        <Card className="shadow-xl bg-brand-surface border-brand-border">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-brand-primary" />
                <CardTitle className="text-xl text-white">About Me</CardTitle>
                {hasBio && <Badge variant="secondary" className="ml-2 bg-green-500/20 text-green-300 hover:bg-green-500/30">✓</Badge>}
              </div>
              <Button
                onClick={() => navigate('/profile/bio')}
                variant="ghost"
                size="sm"
                className="flex items-center gap-1 text-gray-400 hover:text-white hover:bg-white/10"
              >
                <Edit2 className="h-3 w-3" />
                Edit
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {hasBio ? (
              <p className="text-base text-gray-300 whitespace-pre-wrap">{profile.bio}</p>
            ) : (
              <p className="text-sm text-gray-500">No bio written yet</p>
            )}
          </CardContent>
        </Card>

        {/* Looking For Section */}
        <Card className="shadow-xl bg-brand-surface border-brand-border">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Heart className="h-5 w-5 text-brand-primary" />
                <CardTitle className="text-xl text-white">What I'm Looking For</CardTitle>
                {hasLookingFor && <Badge variant="secondary" className="ml-2 bg-green-500/20 text-green-300 hover:bg-green-500/30">✓</Badge>}
              </div>
              <Button
                onClick={() => navigate('/profile/bio')}
                variant="ghost"
                size="sm"
                className="flex items-center gap-1 text-gray-400 hover:text-white hover:bg-white/10"
              >
                <Edit2 className="h-3 w-3" />
                Edit
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {hasLookingFor ? (
              <p className="text-base text-gray-300 whitespace-pre-wrap">{profile.lookingFor}</p>
            ) : (
              <p className="text-sm text-gray-500">Not filled out yet</p>
            )}
          </CardContent>
        </Card>

        {/* Lifestyle Preferences Section */}
        <Card className="shadow-xl bg-brand-surface border-brand-border">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <CardTitle className="text-xl text-white">Lifestyle Preferences</CardTitle>
                {hasLifestyle && <Badge variant="secondary" className="ml-2 bg-green-500/20 text-green-300 hover:bg-green-500/30">✓</Badge>}
              </div>
              <Button
                onClick={() => navigate('/profile/lifestyle')}
                variant="ghost"
                size="sm"
                className="flex items-center gap-1 text-gray-400 hover:text-white hover:bg-white/10"
              >
                <Edit2 className="h-3 w-3" />
                Edit
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {hasLifestyle && profile.lifestylePreferences ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-semibold text-brand-primary">Social Energy</p>
                  <p className="text-base text-gray-300">
                    {getLifestyleLabel(profile.lifestylePreferences.socialLevel, 'social')}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-semibold text-brand-primary">Weekend Energy</p>
                  <p className="text-base text-gray-300">
                    {getLifestyleLabel(profile.lifestylePreferences.weekendEnergy, 'weekend')}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-semibold text-brand-primary">Dating Pace</p>
                  <p className="text-base text-gray-300">
                    {getLifestyleLabel(profile.lifestylePreferences.datingPace, 'pace')}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-semibold text-brand-primary">Communication Style</p>
                  <p className="text-base text-gray-300">
                    {getLifestyleLabel(profile.lifestylePreferences.communicationStyle, 'communication')}
                  </p>
                </div>
              </div>
            ) : (
              <p className="text-sm text-gray-500">No lifestyle preferences set yet</p>
            )}
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <Card className="shadow-xl bg-brand-surface border-brand-border">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <Button
                onClick={handleBack}
                variant="ghost"
                className="flex items-center gap-2 text-gray-400 hover:text-white hover:bg-white/10"
                disabled={completeProfileMutation.isPending}
              >
                <ArrowLeft className="h-4 w-4" />
                Back
              </Button>

              <Button
                onClick={handleCompleteProfile}
                disabled={!isComplete || completeProfileMutation.isPending}
                className="bg-brand-primary hover:bg-brand-primary/90 text-white flex items-center gap-2 rounded-full px-8 py-6 text-lg shadow-lg shadow-brand-primary/20"
              >
                {completeProfileMutation.isPending ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin" />
                    Completing...
                  </>
                ) : (
                  <>
                    <Check className="h-5 w-5" />
                    Complete Profile
                  </>
                )}
              </Button>
            </div>

            {!isComplete && (
              <p className="text-sm text-center text-gray-500 mt-4">
                Complete all sections to activate your profile
              </p>
            )}
          </CardContent>
        </Card>
        </div>
      </div>
    </>
  );
};
